import React, { useEffect, useState } from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton,
    Switch, FormControlLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ExpenseCategoryService } from '../../services/expense-category.service';
import { AccountService } from '../../services/account.service';
import { ExpenseCategoryDTO } from '../../interfaces/expense.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { leafAccounts } from '../../utils/postingAccounts';

const ExpenseCategoryManagement: React.FC = () => {
    // Writes are EXPENSE_CATEGORY_MANAGE server-side; EXPENSE_CATEGORY_VIEW (Accountant) is read-only.
    const canManage = usePermission(PERMISSIONS.EXPENSE_CATEGORY_MANAGE);
    const [categories, setCategories] = useState<ExpenseCategoryDTO[]>([]);
    const [expenseAccounts, setExpenseAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [expenseAccountId, setExpenseAccountId] = useState('');
    const [receiptThreshold, setReceiptThreshold] = useState<string>('');
    const [isActive, setIsActive] = useState(true);
    const toast = useToast();

    useEffect(() => {
        loadAll();
        loadAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAll = async () => {
        setLoading(true);
        try {
            const page = await ExpenseCategoryService.listByInstitution(0, 100);
            setCategories(page.content);
        } catch (e: any) {
            toast.showError(e?.response?.data ?? 'Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    const loadAccounts = async () => {
        try {
            const accounts: Account[] = await AccountService.getAccountsByType('EXPENSE');
            setExpenseAccounts(leafAccounts(accounts));
        } catch (e: any) {
            toast.showError('Failed to load expense accounts');
        }
    };

    const openCreate = () => {
        setEditingId(null);
        setName('');
        setExpenseAccountId('');
        setReceiptThreshold('');
        setIsActive(true);
        setDialogOpen(true);
    };

    const openEdit = (c: ExpenseCategoryDTO) => {
        setEditingId(c.id);
        setName(c.name);
        setExpenseAccountId(c.expenseAccountId);
        setReceiptThreshold(c.receiptRequiredAboveAmount != null ? String(c.receiptRequiredAboveAmount) : '');
        setIsActive(c.isActive);
        setDialogOpen(true);
    };

    const save = async () => {
        if (!name.trim() || !expenseAccountId) {
            toast.showError('Name and account are required');
            return;
        }
        const req = {
            name: name.trim(),
            expenseAccountId,
            receiptRequiredAboveAmount: receiptThreshold ? Number(receiptThreshold) : null,
            isActive,
        };
        try {
            if (editingId) await ExpenseCategoryService.update(editingId, req);
            else await ExpenseCategoryService.create(req);
            setDialogOpen(false);
            await loadAll();
            toast.showSuccess('Saved');
        } catch (e: any) {
            toast.showError(e?.response?.data ?? 'Save failed');
        }
    };

    const remove = async (id: string) => {
        if (!window.confirm('Delete this category?')) return;
        try {
            await ExpenseCategoryService.delete(id);
            await loadAll();
            toast.showSuccess('Deleted');
        } catch (e: any) {
            toast.showError(e?.response?.data ?? 'Delete failed');
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="expense.categories.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">Expense Categories</Typography>
                {canManage && <Button variant="contained" data-help="expense.categories.new" onClick={openCreate}>New Category</Button>}
            </Box>

            <TableContainer component={Paper} data-help-section="expense.categories.table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="expense.categories.col.name">Name</TableCell>
                            <TableCell data-help="expense.categories.col.account">Expense Account</TableCell>
                            <TableCell align="right" data-help="expense.categories.col.receipt">Receipt Required Above</TableCell>
                            <TableCell data-help="expense.categories.col.active">Active</TableCell>
                            <TableCell align="right" data-help="expense.categories.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={5}>Loading…</TableCell></TableRow>
                        ) : categories.length === 0 ? (
                            <TableRow><TableCell colSpan={5}>No categories yet.</TableCell></TableRow>
                        ) : categories.map(c => (
                            <TableRow key={c.id}>
                                <TableCell data-help="expense.categories.col.name">{c.name}</TableCell>
                                <TableCell data-help="expense.categories.col.account">{c.expenseAccountName ?? c.expenseAccountId}</TableCell>
                                <TableCell align="right" data-help="expense.categories.col.receipt">
                                    {c.receiptRequiredAboveAmount != null ? c.receiptRequiredAboveAmount : '—'}
                                </TableCell>
                                <TableCell data-help="expense.categories.col.active">{c.isActive ? 'Yes' : 'No'}</TableCell>
                                <TableCell align="right">
                                    {canManage && (
                                        <>
                                            <IconButton size="small" data-help="expense.categories.action.edit" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" data-help="expense.categories.action.delete" onClick={() => remove(c.id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editingId ? 'Edit Category' : 'New Category'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField data-help="expense.categories.field.name" label="Name" value={name} onChange={e => setName(e.target.value)} fullWidth />
                    <AccountAutocomplete data-help="expense.categories.field.account"
                        accounts={expenseAccounts}
                        value={expenseAccountId}
                        onChange={setExpenseAccountId}
                        label="Expense Account"
                    />
                    <TextField data-help="expense.categories.field.receipt" label="Receipt required above (optional)" value={receiptThreshold}
                               onChange={e => setReceiptThreshold(e.target.value)} type="number" fullWidth />
                    <FormControlLabel data-help="expense.categories.field.active"
                        control={<Switch checked={isActive} onChange={e => setIsActive(e.target.checked)} />}
                        label="Active"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={save} variant="contained" data-help="expense.categories.save">Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExpenseCategoryManagement;
